import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { StatusBadge } from './ui';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  DetailShell, Section, Grid, Icon, SubModal, SplitButton,
  FieldText, FieldSelect, FieldToggle, FieldCustom,
} from './detail/DetailShell';
import { usePageActions } from '../contexts/PermissionContext';
import {
  FIELD_TYPE_OPTIONS, ASSET_LIFECYCLE_OPTIONS, ASSET_LIFECYCLE_META,
  ASSET_TYPE_META, ASSET_TYPE_OPTIONS, parseJsonArray, toJsonArrayString,
} from './assetShared';

// Demirbaş Özellik Tanımı detay — GET-by-id YOK; tek kayıt nav-state row ile,
// yoksa listeyi (take=500) çekip id ile bulunarak yüklenir. CRUD POST / PUT/{id} / DELETE/{id}.
const API_BASE = '/api/asset-attribute-defs';

const EMPTY_FORM = {
  textId: '',
  name: '',
  fieldType: 'TEXT',
  isRequired: false,
  unit: '',
  minValue: '',
  maxValue: '',
  optionsText: '',      // her satır bir seçenek — kaydederken optionsJson'a dönüşür
  usedByTypes: [],      // seçili demirbaş tipleri (usedByTypesJson)
  lifecycleStatus: '1',
};

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}
function unwrap(payload) { return pick(payload, 'Data', 'data') ?? payload; }

function rowToForm(row) {
  return {
    textId: pick(row, 'textId', 'TextId') || '',
    name: pick(row, 'name', 'Name') || '',
    fieldType: pick(row, 'fieldType', 'FieldType') || 'TEXT',
    isRequired: !!pick(row, 'isRequired', 'IsRequired'),
    unit: pick(row, 'unit', 'Unit') || '',
    minValue: pick(row, 'minValue', 'MinValue') ?? '',
    maxValue: pick(row, 'maxValue', 'MaxValue') ?? '',
    optionsText: parseJsonArray(pick(row, 'optionsJson', 'OptionsJson')).join('\n'),
    usedByTypes: parseJsonArray(pick(row, 'usedByTypesJson', 'UsedByTypesJson')),
    lifecycleStatus: String(pick(row, 'lifecycleStatus', 'LifecycleStatus') ?? 1),
  };
}

export function AssetAttributeDefDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  const { canAdd, canEdit, canDelete } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;
  usePageMeta(isNew ? 'Yeni Özellik' : 'Özellik Tanımı Detayı', 'Demirbaş özelliği (attribute) tanımı.');

  const [form, setForm] = useState(EMPTY_FORM);
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };
  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));

  const applyAudit = (r) => setAudit({
    createdBy: pick(r, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'),
    createdDate: pick(r, 'createdDate', 'CreatedDate'),
    updatedBy: pick(r, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'),
    updatedDate: pick(r, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate'),
  });

  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    const stateRow = location.state?.row;
    if (stateRow && String(pick(stateRow, 'id', 'Id')) === String(id)) {
      setForm(rowToForm(stateRow));
      applyAudit(stateRow);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('take', '500');
      const res = await fetch(`${API_BASE}?${params.toString()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Özellik tanımı alınamadı (${res.status})`);
      const data = unwrap(await res.json());
      const items = pick(data, 'items', 'Items') ?? (Array.isArray(data) ? data : []);
      const row = (Array.isArray(items) ? items : []).find((r) => String(pick(r, 'id', 'Id')) === String(id));
      if (!row) throw new Error('Kayıt bulunamadı');
      setForm(rowToForm(row));
      applyAudit(row);
    } catch (err) {
      showToast('error', err.message || 'Özellik tanımı alınamadı');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (isNew && location.state?.clone) {
      setForm(location.state.clone);
      setTried(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const isChoice = form.fieldType === 'CHOICE';
  const isBarcode = form.fieldType === 'BARCODE';

  const toggleType = (t) => setForm((c) => {
    const has = c.usedByTypes.includes(t);
    return { ...c, usedByTypes: has ? c.usedByTypes.filter((x) => x !== t) : [...c.usedByTypes, t] };
  });

  const buildBody = () => {
    const options = form.optionsText.split(/\r?\n/).map((x) => x.trim()).filter(Boolean);
    return {
      textId: (form.textId || '').trim() || null,
      name: (form.name || '').trim(),
      fieldType: form.fieldType,
      isRequired: !!form.isRequired,
      unit: (form.unit || '').trim() || null,
      // BARCODE'da min/maks anlamsız — null gönder.
      minValue: (isBarcode || form.minValue === '') ? null : Number(form.minValue),
      maxValue: (isBarcode || form.maxValue === '') ? null : Number(form.maxValue),
      optionsJson: isChoice ? toJsonArrayString(options) : null,
      usedByTypesJson: toJsonArrayString(form.usedByTypes),
      lifecycleStatus: Number(form.lifecycleStatus) || 0,
    };
  };

  const persist = async ({ andNew = false } = {}) => {
    setTried(true);
    if (!String(form.name || '').trim()) { showToast('error', 'Özellik adı zorunlu.'); return; }
    if (!form.fieldType) { showToast('error', 'Alan tipi zorunlu.'); return; }
    // KURAL: CHOICE → optionsJson zorunlu (en az bir seçenek).
    if (isChoice) {
      const options = form.optionsText.split(/\r?\n/).map((x) => x.trim()).filter(Boolean);
      if (options.length === 0) { showToast('error', 'Seçim listesi için en az bir seçenek girin.'); return; }
    }
    setSaving(true);
    try {
      const url = isNew ? API_BASE : `${API_BASE}/${id}`;
      const res = await fetch(url, { method: isNew ? 'POST' : 'PUT', headers: authHeaders(), body: JSON.stringify(buildBody()) });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || `İşlem başarısız (${res.status})`);
      if (isNew) {
        if (andNew) {
          showToast('success', 'Özellik eklendi. Yeni kayda devam edebilirsiniz.');
          setForm(EMPTY_FORM);
          setTried(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        showToast('success', 'Özellik tanımı oluşturuldu.');
        setTimeout(() => navigate('/asset-attribute-defs'), 400);
        return;
      }
      showToast('success', 'Özellik tanımı güncellendi.');
      setTimeout(() => navigate('/asset-attribute-defs'), 400);
    } catch (err) {
      showToast('error', err.message || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => persist();
  const handleClone = () => navigate('/asset-attribute-defs/new', { state: { clone: { ...form, textId: '' } } });

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/${id}`, { method: 'DELETE', headers: authHeaders() });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || `Silme başarısız (${res.status})`);
      showToast('success', 'Özellik tanımı silindi.');
      setTimeout(() => navigate('/asset-attribute-defs'), 400);
    } catch (err) {
      showToast('error', err.message || 'Silme başarısız');
      setSaving(false);
      setDeleteConfirm(false);
    }
  };

  const title = isNew ? 'Yeni Özellik' : (form.name || 'Özellik Tanımı Detayı');
  const stMeta = ASSET_LIFECYCLE_META[Number(form.lifecycleStatus)] || { label: '-', tone: 'neutral' };
  const statusBadge = !isNew && <StatusBadge tone={stMeta.tone}>{stMeta.label}</StatusBadge>;

  const actions = (
    <>
      {!isNew && canDelete && <button className="dps-btn danger" onClick={() => setDeleteConfirm(true)} disabled={saving}><Icon name="x" size={16} /> Sil</button>}
      <button className="dps-btn ghost" onClick={() => navigate('/asset-attribute-defs')} disabled={saving}>İptal</button>
      {canSave && (isNew ? (
        <SplitButton label={saving ? 'Kaydediliyor…' : 'Kaydet'} icon="check" onClick={handleSave} disabled={saving}
          menu={[{ label: 'Kaydet ve Yeni', desc: 'Kaydet, listeye dönme, yeni kayıt aç', icon: 'plus', onClick: () => persist({ andNew: true }) }]} />
      ) : (
        <SplitButton label={saving ? 'Kaydediliyor…' : 'Kaydet'} icon="check" onClick={handleSave} disabled={saving}
          menu={[{ label: 'Klonla', desc: 'Bu kaydı kopyalayıp yeni özellik aç', icon: 'copy', onClick: handleClone }]} />
      ))}
    </>
  );

  return (
    <DetailShell
      crumb={[{ label: 'Demirbaş Özellik Tanımı', to: '/asset-attribute-defs' }, { label: isNew ? 'Yeni Özellik' : 'Özellik Tanımı Detayı', current: true }]}
      status={statusBadge}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'AssetAttributeDef', entityId: id }}
      actions={actions}
      loading={loading}
    >
      <div className="dps-form">
        <Section icon="tag" title="Özellik Bilgileri" subtitle="Ad, alan tipi ve durum">
          <Grid cols={2}>
            <FieldText form={form} set={set} tried={tried} k="name" label="Özellik Adı" req placeholder="örn. Kompresör Markası" />
            <FieldSelect form={form} set={set} k="fieldType" label="Alan Tipi" options={FIELD_TYPE_OPTIONS} req
              hint={isBarcode ? 'Barkod tipinde min/maks pasiftir.' : (isChoice ? 'Seçim listesi için seçenekler zorunludur.' : undefined)} />
            <FieldText form={form} set={set} k="textId" label="Kod (TextId)" />
            <FieldSelect form={form} set={set} k="lifecycleStatus" label="Durum" options={ASSET_LIFECYCLE_OPTIONS} req />
            <FieldToggle form={form} set={set} k="isRequired" label="Zorunlu Alan" hint="Demirbaş kaydında bu özellik boş bırakılamaz" />
          </Grid>
        </Section>

        <Section icon="shield" title="Değer Kuralları" subtitle="Birim, sayısal aralık ve seçim listesi">
          <Grid cols={3}>
            <FieldText form={form} set={set} k="unit" label="Birim" placeholder="örn. °C, W, adet" />
            <FieldText form={form} set={set} k="minValue" label="Min. Değer" num readOnly={isBarcode}
              placeholder={isBarcode ? 'Barkodda pasif' : ''} />
            <FieldText form={form} set={set} k="maxValue" label="Maks. Değer" num readOnly={isBarcode}
              placeholder={isBarcode ? 'Barkodda pasif' : ''} />
          </Grid>
          {isChoice && (
            <FieldCustom label="Seçenekler (her satır bir seçenek)" req invalid={tried && isChoice && !form.optionsText.trim()} span>
              <textarea
                rows={5}
                value={form.optionsText}
                onChange={(e) => set('optionsText', e.target.value)}
                placeholder={'Küçük\nOrta\nBüyük'}
                style={{
                  width: '100%', resize: 'vertical', borderRadius: 8, padding: '8px 10px',
                  border: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-card, #fff)',
                  color: 'var(--text-primary, #111827)', fontFamily: 'inherit', fontSize: 13,
                }}
              />
            </FieldCustom>
          )}
        </Section>

        <Section icon="box" title="Uygulanacak Demirbaş Tipleri" subtitle="Bu özellik hangi demirbaş tiplerinde görünsün (boş = hepsi)">
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {ASSET_TYPE_OPTIONS.map((opt) => {
              const on = form.usedByTypes.includes(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => toggleType(opt.value)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '6px 14px', borderRadius: 999, cursor: 'pointer', fontWeight: 700, fontSize: 13,
                    border: on ? '1px solid var(--accent, #0073ea)' : '1px solid var(--border-color, #e5e7eb)',
                    background: on ? 'var(--accent-soft, rgba(0, 115, 234,0.12))' : 'var(--bg-card, #fff)',
                    color: on ? 'var(--accent, #0073ea)' : 'var(--text-secondary, #6b7280)',
                  }}
                >
                  {on ? '✓ ' : ''}{ASSET_TYPE_META[opt.value] || opt.label}
                </button>
              );
            })}
          </div>
        </Section>
      </div>

      {deleteConfirm && (
        <SubModal title="Özellik Tanımı Sil" maxWidth={440} onClose={() => setDeleteConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setDeleteConfirm(false)} disabled={saving}>İptal</button>
              <button className="dps-btn danger" onClick={handleDelete} disabled={saving}>{saving ? 'Siliniyor…' : 'Evet, Sil'}</button>
            </>
          )}>
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            <strong>“{form.name || title}”</strong> özellik tanımını silmek istediğinize emin misiniz?
          </p>
        </SubModal>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default AssetAttributeDefDetailPage;
